import React from 'react';
import Image from 'next/image';
import { useFormContext, useWatch } from 'react-hook-form';
import { IPlayNodeForm, NativeSequenceStepForm } from '@/types/plays';
import { LINKEDIN_OUTREACH_LABEL } from '@/utils/playDisplayNames';
import ShadCnFormSelect from '@/components/FormComponents/ShadCnFormSelect';
import ShadCnFormInput from '@/components/FormComponents/ShadCnFormInput';
import ShadCnFormTextarea from '@/components/FormComponents/ShadCnFormTextarea';
import {
  NATIVE_AI_CONTENT_TOKEN,
  NATIVE_CONNECTION_NOTE_MAX,
  NATIVE_MESSAGE_MAX,
  describeNativeWait,
  nativeSequenceEditErrors,
  nativeSequenceLabel,
  nativeSequenceSteps,
  nativeStepFormErrors,
} from '@/utils/nativeLinkedInSequence';

interface INativeLinkedInSequencePanelProps {
  /** The node's persisted custom_action_details (carries `native_sequence`). */
  details: Record<string, unknown>;
  /** People are enrolled: show the sequence, never edit it. */
  frozen: boolean;
  /** The pinned sender, in a rep's words. */
  senderLabel: string;
  /**
   * Seats the sender can be changed to, shown as a picker while nobody is enrolled. Absent for
   * members: sending from a teammate's LinkedIn is an admin's call, same as email.
   */
  senderOptions?: Array<{ label: string; value: string; name: string }>;
}

const NATIVE_SEQUENCE_FROZEN_NOTE = 'This LinkedIn sequence is live — people are enrolled. Clone the campaign to change it.';

const stepTitle = (index: number): string => (
  index === 0 ? '1. Connection request' : `${index + 1}. Message`
);

const WAIT_UNIT_OPTIONS = [
  { label: 'Hours', value: 'HOUR' },
  { label: 'Days', value: 'DAY' },
];

/** What a persisted step sends, in a rep's words — the read-only view of its copy. */
const StepCopy: React.FC<{ step: Record<string, unknown>; isInvite: boolean }> = ({ step, isInvite }) => {
  const template = typeof step.message_template === 'string' ? step.message_template : '';
  const guidance = typeof step.message_guidance === 'string' ? step.message_guidance.trim() : '';
  if (template === NATIVE_AI_CONTENT_TOKEN) {
    return (
      <p className='text-sm text-gray-900 whitespace-pre-wrap'>
        <span className='text-gray-500'>AI-written from brief: </span>
        {guidance || '(no brief)'}
      </p>
    );
  }
  if (isInvite && template === '') {
    return <p className='text-sm text-gray-500'>No note</p>;
  }
  return <p className='text-sm text-gray-900 whitespace-pre-wrap'>{template}</p>;
};

/** One step the user may still reword or re-time (never re-type, add or remove). */
const EditableStep: React.FC<{ index: number; form?: NativeSequenceStepForm }> = ({ index, form }) => {
  const isInvite = index === 0;
  const base = `heyreach.native_steps.${index}`;
  const mode = form?.message_mode ?? 'ai';
  const errors = nativeStepFormErrors(form, index);
  const modeOptions = [
    { label: 'Let AI write it for each prospect', value: 'ai' },
    { label: 'Same message for everyone', value: 'static' },
    ...(isInvite ? [{ label: 'Send with no note', value: 'none' }] : []),
  ];

  return (
    <div className='flex flex-col gap-2 rounded-md border border-gray-200 p-3' data-testid={`native-step-${index}`}>
      <span className='text-sm font-medium text-gray-900'>{stepTitle(index)}</span>

      {!isInvite && (
        <>
          <div className='flex items-end gap-2'>
            <ShadCnFormInput
              name={`${base}.wait_value`}
              label='Wait'
              type='number'
              min={1}
              step={1}
              className='w-24'
            />
            <ShadCnFormSelect
              name={`${base}.wait_unit`}
              options={WAIT_UNIT_OPTIONS}
              className='w-28'
            />
            <span className='pb-2 text-xs text-gray-500'>
              {index === 1 ? 'after acceptance' : 'after the previous message'}
            </span>
          </div>
          {errors.wait && <p className='text-xs text-red-500'>{errors.wait}</p>}
        </>
      )}

      <ShadCnFormSelect
        name={`${base}.message_mode`}
        label={isInvite ? 'Connection note' : 'Message'}
        options={modeOptions}
      />

      {mode === 'ai' && (
        <ShadCnFormTextarea
          name={`${base}.message_guidance`}
          label='Brief for the AI'
          placeholder="e.g. Mention their recent funding round. Keep it under two sentences."
          rows={3}
        />
      )}

      {mode === 'static' && (
        <ShadCnFormTextarea
          name={`${base}.message_template`}
          label='Your message'
          rows={4}
          maxLength={isInvite ? NATIVE_CONNECTION_NOTE_MAX : NATIVE_MESSAGE_MAX}
        />
      )}

      {errors.copy && <p className='text-xs text-red-500'>{errors.copy}</p>}
    </div>
  );
};

/**
 * Side panel for a native LinkedIn sequence node: the connection request and every message after
 * it, each with its wait and its copy. All steps are always shown and always saved together, so a
 * native node is never written back as a single step.
 *
 * Unfrozen (built by the campaign wizard, nobody enrolled yet): the copy and waits can change, the
 * steps themselves can't. Frozen (people are enrolled): read-only, and the campaign is cloned to
 * change it.
 */
const NativeLinkedInSequencePanel: React.FC<INativeLinkedInSequencePanelProps> = ({
  details,
  frozen,
  senderLabel,
  senderOptions,
}) => {
  const { control } = useFormContext<IPlayNodeForm>();
  const forms = useWatch({ control, name: 'heyreach.native_steps' }) as NativeSequenceStepForm[] | undefined;
  const chosenSenderId = useWatch({ control, name: 'heyreach.native_sender_id' }) as string | undefined;
  const canChangeSender = !frozen && !!senderOptions;
  const chosenSender = senderOptions?.find((option) => option.value === chosenSenderId);

  const steps = nativeSequenceSteps(details);
  // Everything that blocks a save, including problems already on the play that this panel can't
  // fix (a wait on the invite, AI sender routing): shown, never quietly corrected.
  const saveErrors = frozen ? [] : nativeSequenceEditErrors(details, forms, chosenSenderId);

  return (
    <div className='p-4 flex flex-col gap-4'>
      <div className='flex items-center gap-2 mb-2'>
        <Image src='https://app.heyreach.io/assets/images/logo/heyreach-icon.svg' alt='LinkedIn Outreach' className='h-5 w-5 object-contain' width={20} height={20} />
        <h3 className='text-sm font-medium text-gray-900'>{LINKEDIN_OUTREACH_LABEL}</h3>
      </div>

      {frozen ? (
        <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900'>
          {NATIVE_SEQUENCE_FROZEN_NOTE}
        </div>
      ) : (
        <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800'>
          This node runs the whole LinkedIn sequence: the connection request, then each message once
          they accept. You can change the wording and the waits here; steps can&apos;t be added or removed.
        </div>
      )}

      {canChangeSender ? (
        <div className='flex flex-col gap-1'>
          <ShadCnFormSelect
            name='heyreach.native_sender_id'
            label='Sender'
            options={senderOptions!}
            placeholder={senderLabel}
          />
          {chosenSender && (
            <p className='text-xs text-gray-500' data-testid='native-sender-note'>
              The connection request and messages go out from {chosenSender!.name}&apos;s LinkedIn,
              under their name. The emails in this campaign keep their own senders.
            </p>
          )}
        </div>
      ) : (
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium text-gray-500'>Sender</span>
          <span className='text-sm text-gray-900'>{senderLabel}</span>
        </div>
      )}

      <div className='flex flex-col gap-1'>
        <span className='text-xs font-medium text-gray-500'>Sending days</span>
        <span className='text-sm text-gray-900'>
          {details.send_on_weekends === true ? 'Every day, including weekends' : 'Weekdays only'}
        </span>
      </div>

      <div className='flex flex-col gap-3 border-t border-gray-100 pt-4'>
        <h4 className='text-sm font-medium text-gray-900'>{nativeSequenceLabel(details)}</h4>
        {steps.map((step, index) => (frozen ? (
          <div
            key={typeof step.step_id === 'string' ? step.step_id : index}
            className='flex flex-col gap-1 rounded-md border border-gray-200 p-3'
            data-testid={`native-step-${index}`}
          >
            <span className='text-sm font-medium text-gray-900'>{stepTitle(index)}</span>
            {index > 0 && (
              <span className='text-xs text-gray-500'>{describeNativeWait(step.wait, index === 1)}</span>
            )}
            <StepCopy step={step} isInvite={index === 0} />
          </div>
        ) : (
          <EditableStep
            key={typeof step.step_id === 'string' ? step.step_id : index}
            index={index}
            form={forms?.[index]}
          />
        )))}
        {saveErrors.length > 0 && (
          <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900'>
            <p className='font-medium'>Changes aren&apos;t saved until these are fixed:</p>
            <ul className='mt-1 list-disc pl-4'>
              {saveErrors.map((error) => <li key={error}>{error}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div className='bg-blue-50 border border-blue-200 rounded-md p-3'>
        <p className='text-xs text-blue-800'>
          <strong>Cost:</strong> 20 credits per lead added to campaign.
          <br />
          Leads flow into this LinkedIn sequence from previous plays in the workflow.
        </p>
      </div>
    </div>
  );
};

export default NativeLinkedInSequencePanel;
